import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, X } from "react-feather";
import AdminHeader from "./Header";
import { adminReqConfig, api } from "../../include/api";
import { formatShortMonthDateYearWithTime } from "../../include/function";
import "../../styles/CategoryProductList.css";

const STATUS_OPTIONS = [
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const getStatusColors = (status) => {
  switch (status) {
    case "approved":
      return { color: "#46A27B", bg: "#D5F5E7" };
    case "submitted":
      return { color: "#CF9500", bg: "#FFF3BA" };
    case "rejected":
      return { color: "#E54611", bg: "#FDDFD0" };
    default:
      return { color: "#333", bg: "#f5f5f5" };
  }
};

const statusLabel = (status) =>
  STATUS_OPTIONS.find((opt) => opt.value === status)?.label || status || "N/A";

const formatAmount = (amount) => {
  const value = Number(amount);
  if (Number.isNaN(value)) return amount || "N/A";
  return value.toLocaleString("en-IN", { style: "currency", currency: "INR" });
};

const ListPaymentProof = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterWrapperRef = useRef(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [proofList, setProofList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [filterValues, setFilterValues] = useState({ status: "" });

  useEffect(() => {
    setSearchQuery(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    setFetchLoading(true);
    api
      .get("/payment_proof/", adminReqConfig())
      .then((response) => {
        const rows = response.status === 200 && Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setProofList(rows);
        setFilteredData(rows);
      })
      .catch(() => {
        setProofList([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  }, []);

  useEffect(() => {
    let result = [...proofList];
    if (filterValues.status) {
      result = result.filter((row) => row.status === filterValues.status);
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      result = result.filter((row) =>
        String(row.orderId ?? "").toLowerCase().includes(query) ||
        String(row.customerId ?? "").toLowerCase().includes(query) ||
        String(row.utr ?? "").toLowerCase().includes(query) ||
        String(row.upiId ?? "").toLowerCase().includes(query) ||
        statusLabel(row.status).toLowerCase().includes(query)
      );
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [searchQuery, filterValues, proofList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterWrapperRef.current && !filterWrapperRef.current.contains(event.target)) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    showingFrom = filteredData.length === 0 ? 0 : indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }

  const clearFilters = () => {
    setFilterValues({ status: "" });
    setSearchQuery("");
    setIsFilterPanelOpen(false);
  };

  return (
    <AdminHeader title={"PAYMENT PROOF"}>
      <div className="content-wrapper p-0">
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          {fetchLoading ? (
            <p>Loading payment proofs...</p>
          ) : (
            <div className="rounded-5 bg-white py-24">
              <div className="row align-items-center px-24 py-10 mb-3">
                <div className="col-auto">
                  <h6 className="mb-0">Payment Proof List</h6>
                </div>
                <div className="col d-flex align-items-center">
                  <div
                    className="filter-wrapper"
                    style={{ position: "relative", width: "500px" }}
                    ref={filterWrapperRef}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #c6d6f5",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        width: "100%",
                      }}
                    >
                      <img
                        src="/assets/images/search-icon.png"
                        alt=""
                        style={{ width: "18px", marginLeft: "8px" }}
                      />
                      <input
                        type="text"
                        placeholder="Search by order, customer, UTR, or status..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFilterPanelOpen(true)}
                        style={{
                          border: "none",
                          outline: "none",
                          padding: "8px 8px",
                          width: "100%",
                          fontSize: "14px",
                          color: "#333",
                          backgroundColor: "transparent",
                        }}
                      />
                      {searchQuery && (
                        <X
                          size={24}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchQuery("");
                            setCurrentPage(1);
                          }}
                          style={{
                            position: "absolute",
                            right: "40px",
                            cursor: "pointer",
                            color: "#999",
                            padding: "4px",
                            zIndex: 10,
                          }}
                        />
                      )}
                      <ChevronDown
                        size={16}
                        style={{
                          marginRight: "8px",
                          color: "#999",
                          cursor: "pointer",
                          transform: isFilterPanelOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFilterPanelOpen(!isFilterPanelOpen);
                        }}
                      />
                    </div>
                    {isFilterPanelOpen && (
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          width: "500px",
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                          boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                          zIndex: 1000,
                          padding: "16px",
                          marginTop: "10px",
                        }}
                      >
                        <div className="row g-3">
                          <div className="col-md-6">
                            <select
                              className="form-select filter-input"
                              value={filterValues.status}
                              onChange={(e) =>
                                setFilterValues({ status: e.target.value })
                              }
                            >
                              <option value="">Status</option>
                              {STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-3">
                          <button
                            type="button"
                            onClick={() => setIsFilterPanelOpen(false)}
                            style={{
                              color: "#fff",
                              backgroundColor: "#2b95d3",
                              borderRadius: "6px",
                              fontSize: "14px",
                              fontWeight: "700",
                              padding: "8px 24px",
                              border: "none",
                            }}
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={clearFilters}
                            style={{
                              color: "#2692d2",
                              backgroundColor: "#d9ebff",
                              borderRadius: "6px",
                              fontSize: "14px",
                              fontWeight: "700",
                              padding: "8px 24px",
                              border: "none",
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="table-responsive mt-24 product-table">
                <table className="table table-striped custom-striped-table-product-list align-middle">
                  <thead>
                    <tr>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Order ID</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Customer ID</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>UTR</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Amount</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>UPI ID</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Date & Time</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Status</th>
                      <th scope="col" style={{ fontSize: "15px", textWrap: "nowrap" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.length > 0 ? (
                      currentItems.map((proof) => {
                        const colors = getStatusColors(proof.status);
                        const when = proof.reviewedAt || proof.createTimestamp || proof.createdAt;
                        return (
                          <tr key={proof.id}>
                            <td>{proof.orderId ?? "N/A"}</td>
                            <td>{proof.customerId ?? "N/A"}</td>
                            <td>{proof.utr || "N/A"}</td>
                            <td style={{ textWrap: "nowrap" }}>{formatAmount(proof.amount)}</td>
                            <td>{proof.upiId || "N/A"}</td>
                            <td style={{ textWrap: "nowrap" }}>
                              {when ? formatShortMonthDateYearWithTime(when) : "N/A"}
                            </td>
                            <td>
                              <span
                                style={{
                                  padding: "6px 12px",
                                  border: `1px solid ${colors.color}`,
                                  borderRadius: "6px",
                                  backgroundColor: colors.bg,
                                  fontWeight: "500",
                                  color: colors.color,
                                  display: "inline-block",
                                  fontSize: "14px",
                                  textWrap: "nowrap",
                                }}
                              >
                                {statusLabel(proof.status)}
                              </span>
                            </td>
                            <td>
                              <button
                                className="list-doc-action-btn"
                                onClick={() => navigate(`/admin/sales/paymentProof/${proof.id}`)}
                              >
                                <img
                                  src="/assets/images/view-icon.png"
                                  alt="View"
                                  style={{ width: "16px", height: "16px", objectFit: "contain" }}
                                />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          No payment proofs found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                <div className="d-flex align-items-center">
                  <select
                    className="form-select d-inline-block w-auto"
                    value={itemsPerPage}
                    onChange={(e) => {
                      const val = e.target.value;
                      setItemsPerPage(val === "all" ? "all" : parseInt(val, 10));
                      setCurrentPage(1);
                    }}
                    style={{ height: "34px", fontSize: "14px", minWidth: "70px" }}
                  >
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="all">All</option>
                  </select>
                  <span className="ms-2">
                    Showing {showingFrom} to {showingTo} of {filteredData.length} Lists
                  </span>
                </div>
                <nav>
                  <ul className="pagination mt-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                        &laquo;
                      </button>
                    </li>
                    {Array.from({ length: totalPage }, (_, i) => i + 1)
                      .filter((pageNum) => {
                        const start = Math.max(1, currentPage - 2);
                        const end = Math.min(totalPage, start + 4);
                        return pageNum >= Math.max(1, end - 4) && pageNum <= end;
                      })
                      .map((pageNum) => (
                        <li
                          key={pageNum}
                          className={`page-item ${currentPage === pageNum ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                            {pageNum}
                          </button>
                        </li>
                      ))}
                    <li className={`page-item ${currentPage === totalPage ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                        &raquo;
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </section>
      </div>
    </AdminHeader>
  );
};

export default ListPaymentProof;
